import axios from "axios";
import { getWorkspaceCode } from "../config/opcoWorkspace";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token storage
let accessToken = null;
let tokenExpiresAt = 0; // epoch ms - parsed from the JWT 'exp' claim

// Token refresh callback - set by AuthContext so the interceptor can silently
// acquire a new token from MSAL without dispatching session-expired.
let tokenRefreshCallback = null;

export const setAccessToken = (token) => {
  accessToken = token;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      tokenExpiresAt = (payload.exp || 0) * 1000;
    } catch {
      tokenExpiresAt = 0;
    }
  } else {
    tokenExpiresAt = 0;
  }
};

export const getAccessToken = () => accessToken;

// OpCo workspace. With the flag off (or outside /o/<code>/) both helpers are
// no-ops, so nothing about a request changes.
export const OPCO_HEADER = "X-OpCo";

// Headers for raw fetch() callers: {"X-OpCo": code} or {}.
export const opcoHeaders = () => {
  const code = getWorkspaceCode();
  return code ? { [OPCO_HEADER]: code } : {};
};

// For EventSource / <a href> / window.open, which cannot send headers:
// appends ?opco=<code> (the backend reads it as a fallback).
export const withOpcoParam = (url) => {
  const code = getWorkspaceCode();
  if (!code || typeof url !== "string") return url;
  const hashIndex = url.indexOf("#");
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}opco=${encodeURIComponent(code)}${hash}`;
};

export const setTokenRefreshCallback = (cb) => {
  tokenRefreshCallback = cb;
};

// Guard against multiple concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];
let proactiveRefreshPromise = null;

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

const refreshAccessTokenOnce = async () => {
  if (!tokenRefreshCallback) return null;
  if (proactiveRefreshPromise) return proactiveRefreshPromise;

  proactiveRefreshPromise = (async () => {
    const freshToken = await tokenRefreshCallback();
    if (freshToken) {
      setAccessToken(freshToken);
    }
    return freshToken;
  })().finally(() => {
    proactiveRefreshPromise = null;
  });

  return proactiveRefreshPromise;
};

// Proactively refresh the token before it expires so API calls never go
// out with a stale token.  MSAL's acquireTokenSilent returns from cache
// instantly when the token is still valid - no extra network call in the
// common case.  We refresh 5 minutes before expiry.
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

api.interceptors.request.use(
  async (config) => {
    if (
      tokenRefreshCallback &&
      tokenExpiresAt > 0 &&
      Date.now() >= tokenExpiresAt - TOKEN_REFRESH_BUFFER_MS
    ) {
      try {
        await refreshAccessTokenOnce();
      } catch (err) {
        console.warn("Proactive token refresh failed:", err);
      }
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    const opcoCode = getWorkspaceCode();
    if (opcoCode) {
      config.headers[OPCO_HEADER] = opcoCode;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// A change to a template's fields can put a connector's Map Payload out of
// sync; the backend then lists the affected connectors as `mapping_impact`.
// Many screens change fields (field editor, bulk delete, imports, JSON
// packages), so this is surfaced once here — App.jsx shows the notice and the
// template page refreshes its connector status — rather than at every caller.
const announceMappingImpact = (response) => {
  const impact = response?.data?.mapping_impact;
  if (Array.isArray(impact) && impact.length > 0) {
    window.dispatchEvent(
      new CustomEvent("mapping-impact", {
        detail: { impact },
      }),
    );
  }
  return response;
};

// Response interceptor - on 401, attempt a silent MSAL token refresh once
// before giving up and dispatching session-expired.
api.interceptors.response.use(
  announceMappingImpact,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // First 401 for this request - try to silently refresh the token
      if (tokenRefreshCallback) {
        if (isRefreshing) {
          // Another request is already refreshing - wait for it
          return new Promise((resolve) => {
            addRefreshSubscriber((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              originalRequest._retry = true;
              resolve(api(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const newToken = await tokenRefreshCallback();
          if (newToken) {
            accessToken = newToken;
            isRefreshing = false;
            onTokenRefreshed(newToken);
            // Retry the original request with the fresh token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            originalRequest._retry = true;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Refresh failed or no callback - truly expired, dispatch session-expired
      console.error(
        "Unauthorized request - token refresh failed, session expired",
      );
      window.dispatchEvent(
        new CustomEvent("session-expired", {
          detail: {
            message: "Your session has expired. Please sign in again.",
          },
        }),
      );
    }
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === "OPCO_FORBIDDEN"
    ) {
      // Not allowed in this workspace: tell the shell (it offers a switch),
      // but do not lock the whole app like a blocked account.
      window.dispatchEvent(
        new CustomEvent("opco-forbidden", {
          detail: { url: originalRequest?.url, workspace: getWorkspaceCode() },
        }),
      );
      return Promise.reject(error);
    }
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === "Access Denied"
    ) {
      // Account deactivated or blocked - dispatch custom event for UI to handle
      window.dispatchEvent(
        new CustomEvent("account-blocked", {
          detail: {
            message: error.response.data.message,
          },
        }),
      );
    }
    if (
      error.response?.status === 402 &&
      error.response?.data?.code === "SUBSCRIPTION_INACTIVE"
    ) {
      // New processing is paused (src/entitlement.py); SubscriptionBanner shows why.
      window.dispatchEvent(
        new CustomEvent("subscription-inactive", { detail: error.response.data }),
      );
    }
    return Promise.reject(error);
  },
);

// =====================
// Requests API
// =====================

export const requestsApi = {
  list: (params = {}) => api.get("/requests", { params }),
  get: (id) => api.get(`/requests/${id}`),
  create: (data) => api.post("/requests", data),
  update: (id, data) => api.put(`/requests/${id}`, data),
  updateStatus: (id, status) => api.put(`/requests/${id}/status`, { status }),
  delete: (id) => api.delete(`/requests/${id}`),

  // Documents
  getDocuments: (id) => api.get(`/requests/${id}/documents`),
  addDocument: (id, documentId, role = "primary") =>
    api.post(`/requests/${id}/documents`, { document_id: documentId, role }),
  removeDocument: (id, documentId) =>
    api.delete(`/requests/${id}/documents/${documentId}`),

  // Versions and Fields
  getVersions: (id) => api.get(`/requests/${id}/versions`),
  createVersion: (id, data) => api.post(`/requests/${id}/versions`, data),
  getFields: (id) => api.get(`/requests/${id}/fields`),
  updateFields: (id, fields, reason = null, options = {}) =>
    api.put(`/requests/${id}/fields`, {
      fields,
      reason,
      ...(options || {}),
    }),

  // AI learning
  getAiLearningSuggestions: (id, data) =>
    api.post(`/requests/${id}/ai-learning/suggestions`, data),
  listAiLearningRules: (id, params = {}) =>
    api.get(`/requests/${id}/ai-learning/rules`, { params }),
  upsertAiLearningRule: (id, data) =>
    api.post(`/requests/${id}/ai-learning/rules`, data),

  // Field Alternatives
  getFieldAlternatives: (requestId, fieldName, versionId = null) => {
    const params = versionId ? { version_id: versionId } : {};
    return api.get(
      `/requests/${requestId}/fields/${encodeURIComponent(fieldName)}/alternatives`,
      { params },
    );
  },
  setFieldActive: (requestId, fieldId) =>
    api.post(`/requests/${requestId}/fields/${fieldId}/set-active`),

  // Actions
  analyze: (id) => api.post(`/requests/${id}/analyze`),
  normalise: (id, fieldIds = null) =>
    api.post(
      `/requests/${id}/normalise`,
      fieldIds ? { field_ids: fieldIds } : {},
    ),
  approve: (id) => api.post(`/requests/${id}/approve`),
  sendForReview: (id) => api.post(`/requests/${id}/send-for-review`),
  assign: (id, userId) =>
    api.post(`/requests/${id}/assign`, userId ? { user_id: userId } : {}),
  // Who an admin may assign this request to (OpCo on: members of its OpCo).
  assignableUsers: (id) => api.get(`/requests/${id}/assignable-users`),
  cancel: (id, reason) => api.post(`/requests/${id}/cancel`, { reason }),

  // Upload document to request
  uploadDocument: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/requests/${id}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Audit Logs
  getAuditLogs: (id, params = {}) =>
    api.get(`/requests/${id}/audit-logs`, { params }),
  getFieldAuditLogs: (requestId, fieldId, params = {}) =>
    api.get(`/requests/${requestId}/fields/${fieldId}/audit-logs`, { params }),
  getFieldsAuditSummary: (requestId) =>
    api.get(`/requests/${requestId}/fields-audit-summary`),

  // Cost Details
  getCostDetails: (id) => api.get(`/requests/${id}/cost-details`),
};

// =====================
// Emails API
// =====================

export const emailsApi = {
  list: (params = {}) => api.get("/emails", { params }),
  stats: (params = {}) => api.get("/emails/stats", { params }),
  get: (id) => api.get(`/emails/${id}`),
  ingest: (data) => api.post("/emails/ingest", data),
  delete: (id) => api.delete(`/emails/${id}`),
  checkDuplicates: (data) => api.post("/emails/check-duplicates", data),

  getDocuments: (id) => api.get(`/emails/${id}/documents`),
  getRequests: (id) => api.get(`/emails/${id}/requests`),
  analyzeBody: (id) => api.post(`/emails/${id}/body-analysis`),
};

// =====================
// Documents API
// =====================

export const documentsApi = {
  list: (params = {}) => api.get("/documents", { params }),
  get: (id) => api.get(`/documents/${id}`),
  upload: (file, source = "direct_upload", requestId = null) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("source", source);
    if (requestId) {
      formData.append("request_id", requestId);
    }
    return api.post("/documents/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  download: (id) =>
    api.get(`/documents/${id}/download`, { responseType: "blob" }),
  getSasUrl: (id, expiryHours = 24) =>
    api.get(`/documents/${id}/sas-url`, {
      params: { expiry_hours: expiryHours },
    }),
  // Get PDF via backend proxy - uses fetch with auth token to avoid CORS and axios binary issues
  // Falls back to /download endpoint if /pdf endpoint not available
  getPdfUrl: (id) => `${API_BASE_URL}/documents/${id}/pdf`,
  getPdfBlob: async (id) => {
    // Use native fetch for binary data to avoid axios issues
    const token = accessToken;

    // Try the /pdf endpoint first, then fall back to /download?inline=true
    const endpoints = [
      `${API_BASE_URL}/documents/${id}/pdf`,
      `${API_BASE_URL}/documents/${id}/download?inline=true`,
    ];

    // Retry config for 503 (backend cold-start / restart)
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000; // 2s, 4s, 8s backoff

    let lastError = null;
    for (const url of endpoints) {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          // Use AbortController for timeout (120s for large PDFs)
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 120000);

          const response = await fetch(url, {
            method: "GET",
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...opcoHeaders(),
            },
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer();
            return { data: arrayBuffer };
          }

          // Retry on 503 Service Unavailable (backend cold-starting)
          if (response.status === 503 && attempt < MAX_RETRIES) {
            const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
            console.warn(
              `getPdfBlob: 503 on ${url}, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`,
            );
            await new Promise((r) => setTimeout(r, delay));
            continue;
          }

          // If not OK, store error and try next endpoint
          const errorText = await response.text();
          lastError = new Error(`HTTP ${response.status}: ${errorText}`);
          break; // non-retryable status, move to next endpoint
        } catch (err) {
          if (err.name === "AbortError") {
            lastError = new Error(
              "PDF download timed out. The file may be too large.",
            );
            break;
          }
          // Network errors (including CORS blocks from 503) are retryable
          if (attempt < MAX_RETRIES) {
            const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
            console.warn(
              `getPdfBlob: network error on ${url}, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`,
              err.message,
            );
            await new Promise((r) => setTimeout(r, delay));
            continue;
          }
          lastError = err;
        }
      }
    }

    throw lastError || new Error("Failed to fetch PDF from all endpoints");
  },
  getAnalysis: (id) => api.get(`/documents/${id}/analysis`),
  analyze: (id) => api.post(`/documents/${id}/analyze`),
  getRequests: (id) => api.get(`/documents/${id}/requests`),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  reprocess: (id) => api.post(`/documents/${id}/reprocess`),
};

// =====================
// Jobs API
// =====================

export const jobsApi = {
  list: (params = {}) => api.get("/jobs", { params }),
  get: (id) => api.get(`/jobs/${id}`),
  getStatus: (id) => api.get(`/jobs/${id}/status`),
  getProcessingLog: (id) => api.get(`/jobs/${id}/processing-log`),
  cancel: (id) => api.post(`/jobs/${id}/cancel`),
  retry: (id) => api.post(`/jobs/${id}/retry`),
  // Mark a job's result as consumed so callers (e.g. the AI-suggest panel)
  // stop re-surfacing it on subsequent loads.
  consume: (id) => api.post(`/jobs/${id}/consume`),
  getStats: () => api.get("/jobs/stats"),
};

// =====================
// Dashboard API
// =====================

export const dashboardApi = {
  getStats: (params = {}) => api.get("/dashboard/stats", { params }),
  getRecentRequests: (limit = 10) =>
    api.get("/dashboard/recent-requests", { params: { limit } }),
  getRecentEmails: (limit = 10) =>
    api.get("/dashboard/recent-emails", { params: { limit } }),
  getPendingReview: (limit = 10) =>
    api.get("/dashboard/pending-review", { params: { limit } }),
  getProcessing: (limit = 10) =>
    api.get("/dashboard/processing", { params: { limit } }),
  getActivity: (limit = 20) =>
    api.get("/dashboard/activity", { params: { limit } }),
  getActivityPaginated: (params = {}) =>
    api.get("/dashboard/activity/all", { params }),
  getTopIssuers: (limit = 4) =>
    api.get("/dashboard/top-issuers", { params: { limit } }),
  getIssuersPaginated: (params = {}) =>
    api.get("/dashboard/issuers/all", { params }),
  getTrend: (days = 7, fromDate = null, toDate = null) => {
    const params =
      fromDate && toDate ? { from: fromDate, to: toDate } : { days };
    return api.get("/dashboard/trend", { params });
  },
  getSystemHealth: () => api.get("/dashboard/system-health"),
  getRequestsByStatus: () => api.get("/dashboard/requests-by-status"),
  getJobStats: () => api.get("/dashboard/job-stats"),
  exportReport: (params = {}) =>
    api.get("/dashboard/export-report", {
      params,
      responseType: "blob",
    }),
};

// =====================
// Templates API
// =====================

function suggestionForm(files, prompt, { restrict = false, detailLevel, templateName } = {}) {
  const formData = new FormData();
  const fileList = Array.isArray(files) ? files : [files];
  fileList.forEach((f) => formData.append("files", f));
  if (prompt) formData.append("prompt", prompt);
  if (restrict) formData.append("restrict", "true");
  if (detailLevel) formData.append("detail_level", detailLevel);
  if (templateName) formData.append("template_name", templateName);
  return formData;
}

export const templatesApi = {
  list: (params = {}) => api.get("/templates", { params }),
  get: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post("/templates", data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
  getCategories: () => api.get("/templates/categories"),
  buildAnalyzer: (templateId, data = {}) =>
    api.post(`/templates/${templateId}/build-analyzer`, data),

  // Activity / audit timeline
  getActivity: (templateId, params = {}) =>
    api.get(`/templates/${templateId}/activity`, { params }),

  // AI learning
  listAiLearningRules: (templateId, params = {}) =>
    api.get(`/templates/${templateId}/ai-learning/rules`, { params }),

  // AI-powered field suggestion (async - returns job_id). options:
  // { restrict: only the fields named in the prompt,
  //   detailLevel: "essentials" | "standard" | "everything" }
  suggestFields: (templateId, files, prompt = "", options = {}) =>
    api.post(
      `/templates/${templateId}/suggest-fields`,
      suggestionForm(files, prompt, options),
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000, // 60s - just queuing, not waiting for result
      },
    ),
  // The same before the template exists (New Template wizard).
  suggestFieldsDraft: (files, prompt = "", options = {}) =>
    api.post("/templates/suggest-fields", suggestionForm(files, prompt, options), {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    }),
  // Re-run a finished suggestion with answers to its questions:
  // [{ question_id, option_id } | { question_id, other_text }]. Returns a new job_id.
  answerSuggestionQuestions: (jobId, answers) =>
    api.post(`/templates/suggest-fields/${jobId}/answers`, { answers }),

  // Template Fields
  getFields: (templateId, params = {}) =>
    api.get(`/templates/${templateId}/fields`, { params }),
  addField: (templateId, data) =>
    api.post(`/templates/${templateId}/fields`, data),
  // Finished fields filled from the request's e-mail, e.g. ['email_sender']
  addEmailFields: (templateId, sources) =>
    api.post(`/templates/${templateId}/email-fields`, { sources }),
  updateField: (templateId, fieldId, data) =>
    api.put(`/templates/${templateId}/fields/${fieldId}`, data),
  deleteField: (templateId, fieldId) =>
    api.delete(`/templates/${templateId}/fields/${fieldId}`),
  deleteAllFields: (templateId) =>
    api.delete(`/templates/${templateId}/fields`),
  importFields: (templateId, data) =>
    api.post(`/templates/${templateId}/import-fields`, data),

  // JSON template packages (xtract-template/1): validate writes nothing;
  // apply creates a template, or updates one when data.template_id is set.
  validateSpec: (data) => api.post("/templates/spec/validate", data, { timeout: 120000 }),
  applySpec: (data) => api.post("/templates/spec/apply", data, { timeout: 120000 }),
  exportSpec: (templateId) => api.get(`/templates/${templateId}/spec`),
  restoreDefaultAnalyzer: () => api.post("/templates/restore-default-analyzer"),
  listLinkedWebhooks: (templateId) =>
    api.get(`/webhooks/template/${templateId}`),
  linkWebhook: (templateId, webhookId) =>
    api.post(`/webhooks/template/${templateId}/${webhookId}`),
  unlinkWebhook: (templateId, webhookId) =>
    api.delete(`/webhooks/template/${templateId}/${webhookId}`),
  // Save a link's Map Payload. Rejected with 422 + mapping_check when it
  // doesn't match the template's fields.
  saveWebhookMapping: (templateId, webhookId, mapping) =>
    api.put(`/webhooks/template/${templateId}/${webhookId}/mapping`, mapping),
  updateAssignments: (templateId, data) =>
    api.put(`/templates/${templateId}/assignments`, data),
  getAssignableUsers: (templateId, role) =>
    api.get(`/templates/${templateId}/assignable-users`, { params: { role } }),
};

// =====================
// Analyze API (test endpoints)
// =====================

// Connector deliveries of one request, and resending to one connector after
// a blocked or failed delivery.
export const requestConnectorsApi = {
  list: (requestId) => api.get(`/webhooks/requests/${requestId}/deliveries`),
  republish: (requestId, webhookId) =>
    api.post(`/webhooks/requests/${requestId}/republish`, { webhook_id: webhookId }),
};

export const analyzeApi = {
  classify: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/analyze/classify", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  },
  extract: (file, prompt = "", templateId = "") => {
    const formData = new FormData();
    formData.append("file", file);
    if (prompt) formData.append("prompt", prompt);
    if (templateId) formData.append("template_id", templateId);
    return api.post("/analyze/extract", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000, // 5 min - prebuilt-layout + LLM can be slow
    });
  },
};

// =====================
// Auth API
// =====================

export const authApi = {
  getCurrentUser: () => api.get("/auth/me"),
  logout: () => api.post("/auth/logout"),
  updateProfile: (data) => api.put("/auth/profile", data),
  claimAdmin: () => api.post("/auth/claim-admin"),
};

// =====================
// Branding API
// =====================

export const brandingApi = {
  get: () => api.get("/branding"),
  update: (data) => api.put("/branding", data),
  reset: () => api.post("/branding/reset"),
  uploadLogo: (file, type = "logo") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    return api.post("/branding/logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const displayPreferencesApi = {
  get: () => api.get("/display-preferences"),
  update: (data) => api.put("/display-preferences", data),
};

export const notificationsApi = {
  list: (unreadOnly = false, { limit, offset } = {}) =>
    api.get("/notifications", { params: { unread_only: unreadOnly, ...(limit != null ? { limit } : {}), ...(offset != null ? { offset } : {}) } }),
  unreadCount: () => api.get("/notifications/unread-count"),
  streamTicket: () => api.post("/notifications/stream-ticket"),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch("/notifications/read-all"),
  dismiss: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete("/notifications"),
};

// =====================
// Admin API (user management, template access, org settings)
// =====================

export const adminApi = {
  // Users
  listUsers: (params = {}) => api.get("/admin/users", { params }),
  updateUserRole: (userId, role) =>
    api.put(`/admin/users/${userId}/role`, { role }),
  deactivateUser: (userId) => api.delete(`/admin/users/${userId}`),
  reactivateUser: (userId) => api.put(`/admin/users/${userId}/reactivate`),
  revokeInvitation: (userId) =>
    api.post(`/admin/users/${userId}/invite/revoke`),
  resendInvitation: (userId) =>
    api.post(`/admin/users/${userId}/invite/resend`),
  restoreInvitation: (userId) =>
    api.post(`/admin/users/${userId}/invite/restore`),

  // Entra User Search & Provisioning
  searchEntraUsers: (query = "", top = 25) =>
    api.get("/admin/entra/users/search", { params: { q: query, top } }),
  provisionUsers: (users, role) =>
    api.post("/admin/users/provision", { users, role }),

  // Entra App Roles (via Graph API)
  getAppRoles: () => api.get("/admin/roles"),
  getRoleAssignments: () => api.get("/admin/role-assignments"),

  // Organization access mode
  getAccessMode: () => api.get("/admin/org/access-mode"),
  updateAccessMode: (access_mode) =>
    api.put("/admin/org/access-mode", { access_mode }),

  // Template access control
  getTemplateAccessSummary: () => api.get("/admin/templates/access-summary"),
  getTemplateAccess: (templateId) =>
    api.get(`/admin/templates/${templateId}/access`),
  setTemplateAccess: (templateId, data) =>
    api.put(`/admin/templates/${templateId}/access`, data),

  // Customer-owned custom domain
  getCustomDomain: () => api.get("/admin/custom-domain"),
  setCustomDomain: (hostname) => api.post("/admin/custom-domain", { hostname }),
  verifyCustomDomain: () => api.post("/admin/custom-domain/verify"),
  activateCustomDomain: () => api.post("/admin/custom-domain/activate"),
  removeCustomDomain: () => api.delete("/admin/custom-domain"),
};

// =====================
// Auto-fill rules API (fill a field from the e-mail domain an order arrived at)
// =====================

export const autofillRulesApi = {
  list: () => api.get("/admin/autofill-rules"),
  create: (data) => api.post("/admin/autofill-rules", data),
  update: (ruleId, data) => api.put(`/admin/autofill-rules/${ruleId}`, data),
  remove: (ruleId) => api.delete(`/admin/autofill-rules/${ruleId}`),
  testAddress: (address, templateId) => api.post("/admin/autofill-rules/test", { address, template_id: templateId || undefined }),
};

// =====================
// Retention Policies API (data retention / auto-cleanup)
// =====================

export const retentionApi = {
  listPolicies: (params = {}) =>
    api.get("/admin/retention/policies", { params }),
  upsertPolicy: (data) => api.post("/admin/retention/policies", data),
  bulkApply: (data) => api.post("/admin/retention/policies/bulk", data),
  deletePolicy: (policyId) =>
    api.delete(`/admin/retention/policies/${policyId}`),
  preview: (data) => api.post("/admin/retention/preview", data),
  previewItems: (data) => api.post("/admin/retention/preview-items", data),
  runNow: () => api.post("/admin/retention/run-now"),
  getAuditLog: (params = {}) =>
    api.get("/admin/retention/audit-log", { params }),
  // Recycle bin
  getRecycleBin: (params = {}) =>
    api.get("/admin/retention/recycle-bin", { params }),
  getRecycleBinCount: () => api.get("/admin/retention/recycle-bin/count"),
  restoreItem: (itemId) =>
    api.post(`/admin/retention/recycle-bin/${itemId}/restore`),
};

// =====================
// Groups API (user groups + membership + template group access)
// =====================

export const groupsApi = {
  list: () => api.get("/groups"),
  get: (groupId) => api.get(`/groups/${groupId}`),
  create: (data) => api.post("/groups", data),
  update: (groupId, data) => api.put(`/groups/${groupId}`, data),
  delete: (groupId) => api.delete(`/groups/${groupId}`),

  // Entra group import/sync
  searchEntra: (query = "") =>
    api.get("/groups/entra/search", { params: { q: query } }),
  importEntra: (entraGroupId) =>
    api.post("/groups/entra/import", { entra_group_id: entraGroupId }),
  sync: (groupId) => api.post(`/groups/${groupId}/sync`),

  // Membership
  getMembers: (groupId) => api.get(`/groups/${groupId}/members`),
  setMembers: (groupId, userIds) =>
    api.put(`/groups/${groupId}/members`, { user_ids: userIds }),
  addMember: (groupId, userId) =>
    api.post(`/groups/${groupId}/members/${userId}`),
  removeMember: (groupId, userId) =>
    api.delete(`/groups/${groupId}/members/${userId}`),

  // Template group access
  getTemplateGroupAccess: (templateId) =>
    api.get(`/groups/templates/${templateId}/access`),
  setTemplateGroupAccess: (templateId, groupIds) =>
    api.put(`/groups/templates/${templateId}/access`, { group_ids: groupIds }),
};

// =====================
// Billing & Usage API
// =====================

export const billingApi = {
  // `custom` is a { from, to } (YYYY-MM-DD) pair for the "Custom" period —
  // it takes priority over `range` server-side when both are present.
  getSummary: (range = "all", custom = null) =>
    api.get("/billing/summary", { params: custom ? { from: custom.from, to: custom.to } : { range } }),
  getUsage: (range = "3m", custom = null) =>
    api.get("/billing/usage", { params: custom ? { from: custom.from, to: custom.to } : { range } }),
  // Requests per (template, person who raised it): the Templates and People drill-downs.
  getRequestBreakdown: (range = "all", custom = null) =>
    api.get("/billing/request-breakdown", { params: custom ? { from: custom.from, to: custom.to } : { range } }),
  getCosts: () => api.get("/billing/costs"),
  // Whether new documents can be processed; open to every signed-in user.
  getEntitlement: () => api.get("/billing/entitlement"),
  // OpCo admins, All-OpCos view: usage per OpCo plus this month's bill split.
  getByOpco: () => api.get("/billing/by-opco"),
  // The billing months the OpCo split can be shown for (newest first), and one month's
  // split; a closed month is frozen. OpCo admins (All-OpCos view) and OpCo managers.
  getOpcoCycles: () => api.get("/billing/opco-cycles"),
  getOpcoCycle: (start) => api.get("/billing/opco-cycle", { params: { start } }),
};

export const bcApi = {
  getConnectionInfo: () => api.get('/business-central/connection-info'),
  getLookupConfig: () => api.get('/business-central/lookup-config'),
  getEntityTypes: () => api.get('/business-central/entity-types'),
  getConnections: () => api.get('/business-central/connections'),
  testConnection: () => api.post('/business-central/test-connection'),
  testConnectionById: (connectionId) => api.post(`/business-central/connections/${connectionId}/test`),
  // Connection management (administrators). opco_code puts a connection under an OpCo;
  // '' shares it with every OpCo.
  createConnection: (body) => api.post('/business-central/connections', body),
  updateConnection: (connectionId, body) => api.patch(`/business-central/connections/${connectionId}`, body),
  deactivateConnection: (connectionId) => api.delete(`/business-central/connections/${connectionId}`),
  // template_id scopes these to that template's BC connection, which is also
  // the scope of the local master data cache they are served from.
  getVendors: (templateId) => api.get('/business-central/vendors', { params: templateId ? { template_id: templateId } : {} }),
  getItems: (templateId) => api.get('/business-central/items', { params: templateId ? { template_id: templateId } : {} }),
  getPOFields: (templateId) => api.get('/business-central/po-fields', { params: templateId ? { template_id: templateId } : {} }),
  getPOLineFields: (templateId) => api.get('/business-central/po-line-fields', { params: templateId ? { template_id: templateId } : {} }),
  getLookups: (templateId) => api.get('/business-central/lookups', { params: templateId ? { template_id: templateId } : {} }),
  // Local master data cache — the source every review-time validation reads.
  getMasterDataStatus: (templateId) =>
    api.get('/business-central/master-data/status', { params: templateId ? { template_id: templateId } : {} }),
  syncMasterData: (templateId, entities = null) =>
    api.post('/business-central/master-data/sync', {
      ...(templateId ? { template_id: templateId } : {}),
      ...(entities ? { entities } : {}),
    }),
  // Per-template BC operations dashboard: stats, retry queue, recent posts,
  // pending approvals — one call, because the panels are always read together.
  getTemplateDashboard: (templateId) =>
    api.get(`/business-central/templates/${templateId}/dashboard`),
  getDocumentHistory: (requestId) =>
    api.get(`/business-central/documents/${requestId}/history`),
  dismissFailedPost: (requestId) =>
    api.post(`/business-central/documents/${requestId}/dismiss`),
  // Re-attach only the source documents that failed to attach to the posted BC document.
  retryAttachments: (requestId) =>
    api.post(`/business-central/documents/${requestId}/attachments/retry`),
  // An admin confirms a leftover document was removed from BC after a failed rollback.
  clearCleanup: (requestId) =>
    api.post(`/business-central/documents/${requestId}/clear-cleanup`),
  updateMasterDataSettings: (templateId, autoSync, intervalHours) =>
    api.patch('/business-central/master-data/settings', {
      ...(templateId ? { template_id: templateId } : {}),
      auto_sync: autoSync,
      interval_hours: intervalHours,
    }),
  detectFields: (templateId, connectionId) => api.post('/business-central/detect-fields', {
    ...(templateId ? { template_id: templateId } : {}),
    ...(connectionId ? { connection_id: connectionId } : {}),
  }),
  autoMapFields: (xtractFields, bcFields) => api.post('/business-central/auto-map', { xtract_fields: xtractFields, bc_fields: bcFields }),
  createPurchaseOrder: (payload) => api.post('/business-central/purchase-orders', payload),
  preflightDocument: (requestId, headerOverrides = {}, lineOverrides = null) =>
    api.post(`/business-central/documents/${requestId}/preflight`, {
      header_overrides: headerOverrides,
      line_overrides: lineOverrides,
    }),
  postDocument: (requestId, payload) => api.post(`/business-central/documents/${requestId}/post`, payload),
  approveDocument: (requestId, payload) => api.post(`/business-central/documents/${requestId}/approve`, payload),
  validate: (payload) => api.post('/business-central/validate', payload),
  resolveVendor: (payload) => api.post('/business-central/resolve-vendor', payload),
  confirmVendor: (payload) => api.post('/business-central/confirm-vendor', payload),
  retryPost: (payload) => api.post('/business-central/retry', payload),
  discoverSchema: (payload) => api.post('/business-central/discover-schema', payload),
  // Schema drift — compare live BC columns vs current template fields
  checkSchemaDrift: (templateId) => api.get(`/templates/${templateId}/bc-schema-drift`),
  applyDriftColumns: (templateId, columnKeys) => api.post(`/templates/${templateId}/bc-schema-drift`, { column_keys: columnKeys }),
  extractFromDocument: (formData) => api.post('/business-central/extract-from-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  validateMapping: (templateId) => api.post('/business-central/validate-mapping', { template_id: templateId }),
  checkDrift: (templateId) => api.post('/business-central/check-drift', { template_id: templateId }),
  // Canonical field set for an entity type — tells the UI which fields become
  // document LINES (so a null bc_field is by design, not a missing mapping).
  getEntityFieldCatalog: (entityTypeKey) =>
    api.get(`/business-central/entity-types/${entityTypeKey}/field-catalog`),
}

export const nsApi = {
  getConnectionInfo: () => api.get("/netsuite/connection-info"),
  // The NetSuite document types a template can target (Purchase Order, Vendor
  // Bid). Hits neither NetSuite nor the DB, so the create-template dialog can
  // prefetch it on open and render the picker without waiting on the
  // connection probe — same contract as bcApi.getEntityTypes().
  getEntityTypes: () => api.get("/netsuite/entity-types"),
  // Admin "add a document type" flow: search NetSuite's full record-type
  // catalog (cheap, cached bulk list — never a live per-type schema fetch),
  // joined with the background "has data" probe cache, then register exactly
  // one via createRecordType, which does the one-time expensive
  // metadata-catalog fetch for it.
  // `showAll` opts into the "show all record types (N hidden)" escape hatch;
  // omitted/false applies the default has-data filter server-side.
  searchRecordTypes: (q, limit, showAll) =>
    api.get("/netsuite/record-types/search", { params: { q, limit, show_all: showAll || undefined } }),
  // On-demand nudge for the background "has data" probe sweep — bounded to
  // one batch server-side, so this is safe to call every time the picker
  // opens rather than only from a background job.
  triggerRecordTypeProbe: () => api.post("/netsuite/record-types/probe"),
  // Can take up to ~2 minutes for a large custom record — the same latency
  // _fetch_field_catalog documents for Purchase Order's own schema fetch.
  // Give this call a long client-side timeout rather than the default.
  // `lineConfig` is optional: { lineRecordTypeKey, lineLinkField, postMechanics,
  // lineContainerKey } — declares a child/line record the same one-time way
  // Vendor Bid's own header->line link had to be manually confirmed.
  createRecordType: (recordTypeKey, displayLabel, lineConfig) =>
    api.post(
      "/netsuite/record-types",
      {
        record_type_key: recordTypeKey, display_label: displayLabel,
        line_record_type_key: lineConfig?.lineRecordTypeKey || undefined,
        line_link_field: lineConfig?.lineLinkField || undefined,
        post_mechanics: lineConfig?.postMechanics || undefined,
        line_container_key: lineConfig?.lineContainerKey || undefined,
      },
      { timeout: 150000 }
    ),
  testConnection: () => api.post("/netsuite/test-connection"),
  // Setup step 1: account, environment(s) and selectable companies (subsidiaries).
  // Always resolves 200 — an unreachable account comes back as connected:false
  // with an error, which the setup UI needs to render.
  getConnectionScope: () => api.get("/netsuite/connection-scope"),
  // Setup step 2: fetch every lookup + field rule for a template and report the
  // counts that were stored. Synchronous — the user is watching for the result.
  bootstrapTemplate: (templateId, subsidiaryId, forceRefresh = true) =>
    api.post("/netsuite/bootstrap-template", {
      template_id: templateId,
      subsidiary_id: subsidiaryId || undefined,
      force_refresh: forceRefresh,
    }),
  // The review screen's single initialisation call: dropdown data AND, when a
  // templateId is given, the `validation` rule bundle the browser evaluates
  // locally — so no further requests are needed while the user types.
  // forceRefresh bypasses the server-side TTL cache for every lookup collection.
  getLookups: (templateId, forceRefresh = false) =>
    api.get("/netsuite/lookups", {
      params: {
        ...(templateId ? { template_id: templateId } : {}),
        ...(forceRefresh ? { force_refresh: true } : {}),
      },
    }),
  // opts: { sample_size, subsidiary_id, vendor_id, template_id, force_refresh }.
  // Passing template_id also persists the detected fields as that template's rules.
  detectFields: (opts = {}) => api.post("/netsuite/detect-fields", opts),
  getFieldRulesStatus: (templateId) =>
    api.get("/netsuite/field-rules/status", {
      params: { template_id: templateId },
    }),
  getCacheStatus: () => api.get("/netsuite/cache-status"),
  // lookupType omitted → invalidate every cached lookup collection.
  refreshLookups: (lookupType) =>
    api.post("/netsuite/refresh-lookups", {
      lookup_type: lookupType || undefined,
    }),
  // Items live in their own indexed table rather than a lookup blob (~51.7k
  // rows), so they have their own sync/state pair. Syncing pages the whole
  // catalog — an admin action, not something a keystroke triggers.
  syncItemCache: () => api.post("/netsuite/items/sync-cache"),
  getItemCacheState: () => api.get("/netsuite/items/cache-state"),
  createPurchaseOrder: (payload) =>
    api.post("/netsuite/purchase-orders", payload),
  // Vendor Bid posts to its own endpoint: the write is header-then-lines, and
  // its response can report a PARTIAL result (header created, some lines
  // failed) that /purchase-orders has no equivalent of.
  createVendorBid: (payload) => api.post("/netsuite/vendor-bids", payload),
  retryVendorBid: (payload) => api.post("/netsuite/vendor-bids/retry", payload),
  // Generic posting entry point for any document type, including an
  // admin-configured record type that has no dedicated endpoint of its own —
  // 'purchase_order'/'vendor_bid' work here too (the backend just delegates
  // to the same create_purchase_order/create_vendor_bid the dedicated
  // endpoints above call), but only a generic record type's review screen
  // actually needs this, since PO/Bid already have their own screens/calls.
  createNsRecord: (recordTypeKey, payload) =>
    api.post(`/netsuite/records/${encodeURIComponent(recordTypeKey)}`, payload),
  retryNsRecord: (recordTypeKey, payload) =>
    api.post(`/netsuite/records/${encodeURIComponent(recordTypeKey)}/retry`, payload),
  validate: (payload) => api.post("/netsuite/validate", payload),
  resolveVendor: (payload) => api.post("/netsuite/resolve-vendor", payload),
  // Generic reference-field resolver — same scored/gated matching resolveVendor
  // uses (near-exact auto-accept only, tie-breaker guard), for lookup_entity
  // values other than vendors (customers, items, subsidiaries, locations),
  // which previously had no confidence concept at all.
  resolveReference: (payload) => api.post("/netsuite/resolve-reference", payload),
  // The NetSuite record a request was posted to, with its "Open in NetSuite" link.
  getPostedRecord: (requestId) => api.get(`/netsuite/requests/${requestId}/posted-record`),
  confirmVendor: (payload) => api.post("/netsuite/confirm-vendor", payload),
  retryPost: (payload) => api.post("/netsuite/retry", payload),
  // Customer twin of confirmVendor: records a reviewer's pick so the next
  // document with the same name resolves automatically.
  confirmCustomer: (payload) => api.post("/netsuite/customers/confirm", payload),

  // ── Name mappings (document name → NetSuite record lookup table) ─────────
  // Org-scoped, admin/template_manager only. Rows are learned from reviewer
  // confirmations or maintained by hand / CSV here.
  listNameMappings: (params = {}) =>
    api.get("/netsuite/name-mappings", { params }),
  saveNameMapping: (body) => api.post("/netsuite/name-mappings", body),
  deleteNameMapping: (id) =>
    api.delete(`/netsuite/name-mappings/${encodeURIComponent(id)}`),
  importNameMappings: (file, entityType) => {
    const formData = new FormData();
    formData.append("file", file);
    if (entityType) formData.append("entity_type", entityType);
    return api.post("/netsuite/name-mappings/import", formData, {
      params: { entity_type: entityType || undefined },
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  exportNameMappings: (entityType) =>
    api.get("/netsuite/name-mappings/export", {
      params: { entity_type: entityType || undefined },
      responseType: "blob",
    }),
  // Reseller/Customer lookup — Vendor Bid only; PO has no such field.
  searchCustomers: (q, subsidiaryId, limit = 25) =>
    api.get("/netsuite/customers/search", {
      params: { q, subsidiary_id: subsidiaryId || undefined, limit },
    }),
  // `lines` ([{code, description}], Vendor Bid) adds `by_line` to the
  // response: one hit or null per line, carrying `match_source`.
  resolveItems: (codes, subsidiaryId, lines) =>
    api.post("/netsuite/items/resolve", {
      codes,
      subsidiary_id: subsidiaryId || undefined,
      ...(lines ? { lines } : {}),
    }),
  getBidRecordName: (requestId, orgId) =>
    api.get("/netsuite/vendor-bids/record-name", {
      params: { request_id: requestId, org_id: orgId || undefined },
    }),
  searchItems: (q, subsidiaryId) =>
    api.get("/netsuite/items/search", {
      params: { q, subsidiary_id: subsidiaryId || undefined },
    }),
  // Live search across ALL vendors in the account, optionally scoped to a
  // subsidiary — not the cached bulk list.
  searchVendors: (q, subsidiaryId, limit) =>
    api.get("/netsuite/vendors/search", {
      params: {
        q,
        subsidiary_id: subsidiaryId || undefined,
        limit: limit || undefined,
      },
    }),
  extractFromDocument: (formData) =>
    api.post("/netsuite/extract-from-document", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  validateMapping: (templateId) =>
    api.post("/netsuite/validate-mapping", { template_id: templateId }),
  checkDrift: (templateId) =>
    api.post("/netsuite/check-drift", { template_id: templateId }),

  // Per-template NetSuite operations dashboard: stats, retry queue, recent
  // posts, pending approvals — one call, mirroring bcApi.getTemplateDashboard.
  getTemplateDashboard: (templateId) =>
    api.get(`/netsuite/templates/${templateId}/dashboard`),
  getDocumentHistory: (requestId) =>
    api.get(`/netsuite/documents/${requestId}/history`),

  // "Test This Configuration" — creates and deletes ONE real record in the
  // customer's NetSuite account to discover which fields it actually rejects
  // a record without. On a Production account the server demands `confirm`
  // echo the record type key back exactly; a 403 with probe_status
  // 'confirmation_required' or 'skipped' is the expected refusal, not an
  // error to swallow. Can take a few seconds (up to 8 create attempts).
  probeRequiredFields: (recordTypeKey, { templateId, confirm } = {}) =>
    api.post(
      `/netsuite/record-types/${encodeURIComponent(recordTypeKey)}/probe-required`,
      { template_id: templateId || undefined, confirm: confirm || undefined },
      { timeout: 120000 }
    ),
  // How many probe test records are currently unaccounted for — so "we left
  // a record behind in your ERP" is visible rather than only in logs.
  getOutstandingTestRecords: () => api.get("/netsuite/test-records/outstanding"),

  // ── Add-record-type wizard: proof-based line-item-link discovery ──────────
  // Wizard-time-only admin actions backing NsLineLinkWizard.jsx. None of these
  // are part of the production posting path — see the backend's own comment
  // above these routes in netsuite_api.py.
  //
  // Branch 1 check: does record_type_key embed a native line sublist in its
  // own schema (e.g. Purchase Order)? If so nothing else needs configuring.
  detectRecordShape: (recordTypeKey) =>
    api.post("/netsuite/record-types/detect-shape", { record_type_key: recordTypeKey }),
  // Branch 3's own field catalog lookup — never detectFields/detectFields's
  // /detect-fields, which silently falls back to Purchase Order's fields for
  // any record type not yet registered in ns_record_type_config (i.e. every
  // record type this wizard is actively trying to configure).
  getRecordTypeFieldCatalog: (recordTypeKey) =>
    api.get(`/netsuite/record-types/${encodeURIComponent(recordTypeKey)}/field-catalog`),
  // Bounded recent-records browse list for the Branch 2 example picker — ids
  // only, NetSuite's collection listing doesn't cheaply expose a display name.
  listRecords: (recordTypeKey, limit = 25) =>
    api.get(`/netsuite/records/${encodeURIComponent(recordTypeKey)}`, { params: { limit } }),
  // Preview/confirm one record exists (a picked or pasted example id) and read
  // its raw fields back for later use as verify-link's header_fields.
  getRecordById: (recordTypeKey, recordId) =>
    api.get(`/netsuite/records/${encodeURIComponent(recordTypeKey)}/${encodeURIComponent(recordId)}`),
  // Branch 3 guided create: make one real record with exactly these field
  // values. Its own path, distinct from createNsRecord above (which posts to
  // the same base /netsuite/records/<type> URL with a completely different
  // body shape: {request_id, header_fields, line_items} for real production
  // posting vs {fields} here for a wizard-time throwaway/example record).
  createGuidedRecord: (recordTypeKey, fields) =>
    api.post(`/netsuite/records/${encodeURIComponent(recordTypeKey)}/guided-create`, { fields }),
  // Delete one real record — Branch 3's optional example cleanup, and used
  // internally by verify-link's own throwaway-pair cleanup.
  deleteRecordById: (recordTypeKey, recordId) =>
    api.delete(`/netsuite/records/${encodeURIComponent(recordTypeKey)}/${encodeURIComponent(recordId)}`),
  // The core proof scan: which custom record type + field actually references
  // this example id in real data. excludeRecordTypeKey keeps the header type
  // from ever matching itself.
  scanForLineLink: (exampleId, excludeRecordTypeKey) =>
    api.post("/netsuite/records/scan-link", {
      example_id: exampleId,
      exclude_record_type_key: excludeRecordTypeKey || undefined,
    }),
  // The mandatory create-read-delete gate: proves a discovered link field
  // actually resolves before "Add record type" may be enabled.
  verifyLineLink: ({ headerRecordTypeKey, lineRecordTypeKey, lineLinkField, headerFields, lineFields }) =>
    api.post("/netsuite/record-types/verify-link", {
      header_record_type_key: headerRecordTypeKey,
      line_record_type_key: lineRecordTypeKey,
      line_link_field: lineLinkField,
      header_fields: headerFields || {},
      line_fields: lineFields || {},
    }),
};

// =====================
// Output Destinations API (FTP/SFTP CSV Output Connector)
// =====================

export const outputDestinationsApi = {
  // What a customer needs in order to let this deployment reach their systems
  // (chiefly the outbound IP to allow-list). Deployment-specific, so it comes
  // from the server rather than being hardcoded in the UI.
  connectionInfo: () => api.get("/output-destinations/connection-info"),

  // CRUD (AC1/AC2)
  list: () => api.get("/output-destinations"),
  get: (id) => api.get(`/output-destinations/${id}`),
  create: (data) => api.post("/output-destinations", data),
  update: (id, data) => api.put(`/output-destinations/${id}`, data),
  delete: (id, { force = false } = {}) =>
    api.delete(`/output-destinations/${id}${force ? "?force=true" : ""}`),

  // Template linking + field mapping + filename pattern (AC4/AC5)
  listForTemplate: (templateId) =>
    api.get(`/output-destinations/templates/${templateId}`),
  linkToTemplate: (destinationId, templateId, data = {}) =>
    api.post(`/output-destinations/${destinationId}/templates/${templateId}`, data),
  updateTemplateMapping: (destinationId, templateId, data = {}) =>
    api.put(`/output-destinations/${destinationId}/templates/${templateId}`, data),
  unlinkFromTemplate: (destinationId, templateId) =>
    api.delete(`/output-destinations/${destinationId}/templates/${templateId}`),

  // Manual export / re-export (AC7)
  triggerExport: (destinationId, requestId, data = {}) =>
    api.post(`/output-destinations/${destinationId}/export/${requestId}`, data),

  // Connection test (write probe: uploads a tiny file and removes it)
  testConnection: (destinationId) =>
    api.post(`/output-destinations/${destinationId}/test`),

  // Per-destination delivery log ("did last night's exports land?")
  listDeliveriesForDestination: (destinationId, { limit = 50, status } = {}) =>
    api.get(`/output-destinations/${destinationId}/deliveries`, {
      params: { limit, ...(status ? { status } : {}) },
    }),

  // Audit list (AC8)
  listDeliveriesForRequest: (requestId) =>
    api.get(`/requests/${requestId}/output-deliveries`),
  getDelivery: (deliveryId) =>
    api.get(`/requests/output-deliveries/${deliveryId}`),
};

export const boomiApi = {
  getLookups: () => api.get('/boomi/lookups'),
  getOpcos: () => api.get('/boomi/opcos'),
  validateMapping: (fieldMapping) => api.post('/boomi/validate-mapping', { field_mapping: fieldMapping }),
  validateLookups: (opco, itemCodes, customerCodes) => api.post('/boomi/validate-lookups', {
    opco, item_codes: itemCodes || [], customer_codes: customerCodes || [],
  }),
}

// =====================
// OpCo admin API (/api/opco): OpCos, members, domain/mailbox mappings.
// Only reachable when ENABLE_OPCO is on; the UI gates on useOpCo().enabled.
// =====================

export const opcoApi = {
  mine: () => api.get("/opco/mine"),
  list: ({ includeInactive = false } = {}) =>
    api.get("/opco", { params: includeInactive ? { include_inactive: 1 } : {} }),
  create: ({ code, name, emails }) => api.post("/opco", { code, name, ...(emails ? { emails } : {}) }),
  // code is immutable server-side; only name / is_active are sent.
  update: (id, data = {}) => api.patch(`/opco/${id}`, data),

  listMembers: (id) => api.get(`/opco/${id}/members`),
  // Org users for the Add-member picker (OpCo admins and that OpCo's managers).
  candidates: (id) => api.get(`/opco/${id}/candidates`),
  addMember: (id, userId, role = "member") =>
    api.post(`/opco/${id}/members`, { user_id: userId, role }),
  updateMember: (id, userId, data = {}) =>
    api.patch(`/opco/${id}/members/${userId}`, data),
  removeMember: (id, userId) => api.delete(`/opco/${id}/members/${userId}`),
  // CSV bulk assignment of existing users (email,opco,role). opcoId is the OpCo
  // for rows without an opco cell; the server re-checks the file on commit.
  previewMemberImport: (csv, { opcoId } = {}) =>
    api.post("/opco/members/import/preview", { csv, ...(opcoId != null ? { opco_id: opcoId } : {}) }),
  importMembers: (csv, { opcoId } = {}) =>
    api.post("/opco/members/import", { csv, ...(opcoId != null ? { opco_id: opcoId } : {}) }),

  listDomains: () => api.get("/opco/domains"),
  addDomain: (id, kind, value) =>
    api.post(`/opco/${id}/domains`, { kind, value }),
  removeDomain: (domainId) => api.delete(`/opco/domains/${domainId}`),

  // CSV import of the OpCo master list (OpCo admins). Both send the file's text;
  // the server re-checks it on commit, so a stale preview can't be applied.
  previewImport: (csv, { includeAutofillRules = false } = {}) =>
    api.post("/opco/import/preview", { csv, include_autofill_rules: includeAutofillRules }),
  importCsv: (csv, { includeAutofillRules = false } = {}) =>
    api.post("/opco/import", { csv, include_autofill_rules: includeAutofillRules }),

  unassignedCount: () => api.get("/opco/unassigned-count"),

  // Super Admins: OPCO_ADMIN_EMAILS (permanent) plus users granted the role here.
  listAdmins: () => api.get("/opco/admins"),
  grantAdmin: (email) => api.post("/opco/admins", { email }),
  revokeAdmin: (userId) => api.delete(`/opco/admins/${userId}`),

  // Automatic membership by email (Super Admins). Rules start OFF; the switch
  // controls new sign-ins only. Bulk: preview -> apply (with the preview's plan_hash) -> undo a run.
  listAssignRules: () => api.get("/opco/auto-assign/rules"),
  assignDomainStatus: () => api.get("/opco/auto-assign/domain-status"),
  createAssignRule: (data) => api.post("/opco/auto-assign/rules", data),
  updateAssignRule: (id, data) => api.patch(`/opco/auto-assign/rules/${id}`, data),
  deleteAssignRule: (id) => api.delete(`/opco/auto-assign/rules/${id}`),
  testAssignEmail: (email) => api.get("/opco/auto-assign/test", { params: { email } }),
  previewAutoAssign: (adminsAsManagers = false) =>
    api.post("/opco/auto-assign/preview", { admins_as_managers: Boolean(adminsAsManagers) }),
  downloadAutoAssignCsv: (adminsAsManagers = false) =>
    api.get("/opco/auto-assign/preview.csv", {
      params: { admins_as_managers: adminsAsManagers ? 1 : 0 },
      responseType: "blob",
    }),
  applyAutoAssign: (data) => api.post("/opco/auto-assign/apply", data),
  listAssignRuns: () => api.get("/opco/auto-assign/runs"),
  getAssignRun: (uid) => api.get(`/opco/auto-assign/runs/${uid}`),
  undoAssignRun: (uid) => api.post(`/opco/auto-assign/runs/${uid}/undo`),

  // Bring a person's earlier Unassigned requests and templates into an OpCo (Super Admins).
  // Preview writes nothing; apply needs the preview's plan_hash and the keys left out.
  previewWorkMove: (userId, opcoId) =>
    api.post("/opco/work-moves/preview", { user_id: userId, opco_id: opcoId }),
  applyWorkMove: (data) => api.post("/opco/work-moves/apply", data),
  listWorkMoveRuns: () => api.get("/opco/work-moves/runs"),
  getWorkMoveRun: (uid) => api.get(`/opco/work-moves/runs/${uid}`),
  undoWorkMoveRun: (uid) => api.post(`/opco/work-moves/runs/${uid}/undo`),

  // Per-OpCo NetSuite account (OpCo admins). Secrets are write-only: the
  // server returns which are saved (has_*), never their values.
  listNsConnections: () => api.get("/opco/ns-connections"),
  getNsConnection: (id) => api.get(`/opco/${id}/ns-connection`),
  saveNsConnection: (id, data = {}) => api.put(`/opco/${id}/ns-connection`, data),
  deleteNsConnection: (id) => api.delete(`/opco/${id}/ns-connection`),
  testNsConnection: (id) => api.post(`/opco/${id}/ns-connection/test`),
};

// =====================
// Learning (how-to videos). Viewers never come through here: the Learning page
// reads the hub's public catalog with a plain fetch (src/utils/learning.js).
// These editor routes exist only on the hub itself (LEARNING_HUB=true), so
// they use the normal same-origin client; elsewhere they answer 404.
// =====================

export const learningApi = {
  adminMe: () => api.get("/learning/admin/me"),
  adminCatalog: () => api.get("/learning/admin/catalog"),

  createCategory: (data) => api.post("/learning/admin/categories", data),
  updateCategory: (id, data) => api.patch(`/learning/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/learning/admin/categories/${id}`),
  reorderCategories: (ids) => api.post("/learning/admin/categories/reorder", { ids }),

  createItem: (data) => api.post("/learning/admin/items", data),
  updateItem: (id, data) => api.patch(`/learning/admin/items/${id}`, data),
  archiveItem: (id, archived = true) =>
    api.post(`/learning/admin/items/${id}/${archived ? "archive" : "unarchive"}`),
  deleteItem: (id) => api.delete(`/learning/admin/items/${id}`),
  reorderItems: (categoryId, ids) =>
    api.post("/learning/admin/items/reorder", { category_id: categoryId, ids }),
  // mp4 / webm / mov, at most 250 MB. onProgress gets 0-100.
  uploadVideo: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/learning/admin/items/${id}/video`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0,
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
  },
};

export default api;
